import { Box, Button, Chip, Link as MuiLink, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Fragment, useMemo, useState } from "react";
import { Link as RouterLink, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

import type { DocSummary } from "@xop/dashboard-shared";

import {
  BulkActionBar,
  BulkButton,
  BulkCheckbox,
  useBulkSelection,
} from "../../components/bulk-selection/index.js";
import { useDocumentCapabilities } from "../../auth/permissions.js";
import { PageHeader } from "../../components/PageHeader.js";
import { BulkDeleteDialog } from "../../components/ui/bulk-delete-dialog.js";
import { ConfirmDialog } from "../../components/ui/confirm-dialog.js";
import { BackButton } from "@/components/ui/back-button";
import { PageLoader } from "@/components/ui/page-loader";
import { Pagination } from "../../components/ui/pagination.js";
import { usePagedList } from "../../hooks/usePagedList.js";
import {
  bulkDeleteDocs,
  deleteDoc,
  listCategories,
  listDocs,
  listReleases,
  listSolutionAreas,
  publishDoc,
  unpublishDoc,
  updateDoc,
  listAllDocs,
} from "./api.js";
import { ShareToChatDialog } from "../../components/share/ShareToChatDialog.js";
import { DocRow } from "./type-release/DocRow.js";
import { FolderShareMenu } from "./FolderShareMenu.js";
import { folderOptions, MAX_FOLDER_DEPTH } from "./folderOptions.js";
import { MoveToFolderDialog } from "./MoveToFolderDialog.js";
import { buildBulkDownload } from "./docDownload.js";
import { saveBlob } from "../../lib/download.js";
import { toast } from "../../components/ui/use-toast.js";
import { SubFolderList } from "./type-release/SubFolderList.js";
import {
  cycleDocDateSort,
  parseDocDateSort,
  scopeFilter,
  solutionCounts,
  solutionFilter,
  solutionOptions,
  sortByDocumentDate,
} from "./type-release/filters.js";
import { SolutionSwitcher } from "./type-release/SolutionSwitcher.js";
import { documentShareSubject } from "./shareSubject.js";
import { useDocPdfDownload } from "./useDocPdfDownload.js";

/** The document LIST, in one of three shapes (two shelves, 2026-09-13):
 *
 *   - `/documentation/release/:releaseId` — RELEASE DOCUMENTATION: every
 *     document pinned to that release or version, grouped as one list, plus
 *     (for a version) the guides inherited from its release line. The
 *     audience follows the release automatically; the folder's portal tick
 *     does not apply. "+ Add" pre-fills the release.
 *   - `/documentation/type/:slug` — a LIBRARY folder: its unversioned
 *     documents. "+ Add" pre-fills the folder.
 *   - `/documentation/type/:slug/release/:releaseId` — the old FOLDER →
 *     RELEASE pair. Still renders so bookmarks keep working; the tree no
 *     longer hands it out.
 *
 * Every shape carries the View / Publish / Unpublish / Delete actions, the
 * solution-area filter and the sortable "Created" column.
 *
 * This file is the data + render shell. The pure list filters live in
 * `type-release/filters.ts`, the solution-area chip row in `SolutionSwitcher`,
 * one document row in `DocRow`, and the per-row pill/menu pieces in
 * `type-release/rowParts.tsx`. */
export function TypeReleaseDocsPage(): JSX.Element {
  const qc = useQueryClient();
  const pdf = useDocPdfDownload();
  // Documentation authoring is three-tier (view < create < edit). `canCreate`
  // gates the "+ Add" / empty-state create affordances; `canModify(doc)` gates
  // the per-row Edit / publish toggle / Delete + the bulk checkbox — true for a
  // full editor on ANY doc, or for a create-tier author on docs THEY wrote. A
  // view-only role (e.g. a sales_manager without create) sees only View + PDF.
  // The server + portal-api enforce the same rules — this just stops surfacing
  // buttons that would 403.
  const { canCreate, canEditAll, canModify } = useDocumentCapabilities();
  const { slug = "", releaseId = "" } = useParams<{ slug: string; releaseId: string }>();
  // No folder in the URL means the RELEASE page; no release means the
  // folder's LIBRARY shelf.
  const releaseMode = slug === "" && releaseId !== "";
  const isUncategorized = slug === "uncategorized";
  const isUnversioned   = !releaseMode && (releaseId === "" || releaseId === "unversioned");
  const releaseNumericId = isUnversioned ? null : Number(releaseId);

  const { data: cats } = useQuery({
    queryKey: ["docs", "categories"],
    queryFn: listCategories,
  });
  const { data: releases } = useQuery({
    queryKey: ["docs", "releases"],
    queryFn: listReleases,
  });
  const { data: sols } = useQuery({
    queryKey: ["docs", "solution-areas"],
    queryFn: listSolutionAreas,
  });
  // The live managed pillar list — drives the switcher chips (so an empty
  // pillar still shows "(0)"), the per-row re-tag menu, and the count map.
  const areas = useMemo(() => sols?.data ?? [], [sols]);

  const category = useMemo(
    () => (cats?.data ?? []).find((c) => c.slug === slug) ?? null,
    [cats, slug],
  );
  // Two levels of sub-folders (2026-10-31): a folder page links to its
  // sub-folders, and a sub-folder's breadcrumb names every folder above it.
  const subFolders = useMemo(
    () =>
      category
        ? (cats?.data ?? []).filter((c) => c.parent_id === category.id).sort((a, b) => a.name.localeCompare(b.name))
        : [],
    [cats, category],
  );
  const ancestorFolders = useMemo(() => {
    const all = cats?.data ?? [];
    const chain: typeof all = [];
    let parentId = category?.parent_id ?? null;
    while (parentId != null && chain.length < MAX_FOLDER_DEPTH) {
      const parent = all.find((c) => c.id === parentId);
      if (!parent || parent.id === category?.id || chain.includes(parent)) break;
      chain.unshift(parent);
      parentId = parent.parent_id ?? null;
    }
    return chain;
  }, [cats, category]);
  // Sub-folder document counts come from the same cached full list the
  // sidebar tree reads, so this costs no extra request once it is warm.
  const { data: allDocs } = useQuery({
    queryKey: ["docs", "tree", "all"],
    queryFn: () => listAllDocs({ q: "", category_id: "all" }),
    enabled: subFolders.length > 0,
  });
  // A sub-folder row counts its own documents AND the ones in the folders
  // below it — "R11.0 · 12 documents" when they sit in "Command Control".
  const subFolderCounts = useMemo(() => {
    const own = new Map<number, number>();
    for (const d of allDocs ?? []) {
      if (d.default_release || !d.category) continue;
      own.set(d.category.id, (own.get(d.category.id) ?? 0) + 1);
    }
    const all = cats?.data ?? [];
    const total = (id: number, seen: Set<number>): number => {
      if (seen.has(id)) return 0;
      seen.add(id);
      return all.filter((c) => c.parent_id === id).reduce((sum, c) => sum + total(c.id, seen), own.get(id) ?? 0);
    };
    return new Map(subFolders.map((f) => [f.id, total(f.id, new Set())]));
  }, [allDocs, cats, subFolders]);
  const folders = useMemo(() => folderOptions(cats?.data ?? []), [cats]);
  // Documents being moved: one (row menu) or the bulk selection.
  const [moveIds, setMoveIds] = useState<number[] | null>(null);
  const release = useMemo(
    () =>
      releaseNumericId != null
        ? (releases?.data ?? []).find((r) => r.id === releaseNumericId) ?? null
        : null,
    [releases, releaseNumericId],
  );
  // The RELEASE LINE a version belongs to — its guides are inherited.
  const line = useMemo(
    () =>
      release?.parent_release_id != null
        ? (releases?.data ?? []).find((r) => r.id === release.parent_release_id) ?? null
        : null,
    [releases, release],
  );

  const enabled = releaseMode || isUncategorized || category != null;
  const categoryParam = releaseMode || isUncategorized ? "all" : String(category?.id ?? "all");
  const { data: docList, isLoading } = useQuery({
    queryKey: [
      "docs",
      "manage",
      {
        q: "",
        categoryId: categoryParam,
        page: 1,
        per_page: 500,
      },
    ],
    queryFn: () =>
      listDocs({
        q: "",
        category_id: categoryParam,
        page: 1,
        per_page: 500,
      }),
    enabled,
  });

  // Server-side filter is by category only — apply the release filter
  // client-side to keep the API surface unchanged. In release mode the
  // category is "all", so this is every document pinned to the release.
  const filtered = useMemo(
    () => scopeFilter(docList?.data ?? [], { isUncategorized, isUnversioned, releaseNumericId }),
    [docList, isUncategorized, isUnversioned, releaseNumericId],
  );
  // A VERSION inherits the guides written against its release line. Listed
  // read-only here — they are edited on the release's own page.
  const inherited = useMemo(
    () => (releaseMode && line ? (docList?.data ?? []).filter((d) => d.default_release?.id === line.id) : []),
    [releaseMode, line, docList],
  );

  // ── Solution-area switcher ──────────────────────────────────────────
  // The customer's "switch categories" control: an orthogonal product-line
  // filter (Unified Communications / Public Safety / Financial Services)
  // layered on top of the release-scoped list. Lives in the URL (`?solution=`)
  // so it survives reloads + is shareable. "all" (or absent) shows everything;
  // "none" shows the un-tagged bucket so editors can find docs to file.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeSolution = searchParams.get("solution") ?? "all";

  // Per-area counts off the release-scoped list (so each chip shows how many
  // docs it would reveal). Independent of the active selection. A doc with
  // several pillars counts under each.
  const counts = useMemo(() => solutionCounts(filtered, areas), [filtered, areas]);
  // The chips to render: All, each managed pillar, and Uncategorized (only when
  // something is actually un-tagged).
  const options = useMemo(() => solutionOptions(counts, areas), [counts, areas]);

  const setSolution = (value: string): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === "all") next.delete("solution");
        else next.set("solution", value);
        return next;
      },
      { replace: true },
    );
  };

  // ── "Created" column sort ───────────────────────────────────────────
  // Sort by the detected document date (the uploaded PDF's own /CreationDate).
  // URL-driven (`?doc_sort=date_desc|date_asc`, absent = server order) so it
  // survives reloads + is shareable, like the solution filter. Clicking the
  // column header cycles unsorted → newest → oldest → unsorted; undated docs
  // always sink to the bottom.
  const sortDir = parseDocDateSort(searchParams.get("doc_sort"));
  const cycleSort = (): void => {
    const next = cycleDocDateSort(sortDir);
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next) p.set("doc_sort", next);
        else p.delete("doc_sort");
        return p;
      },
      { replace: true },
    );
  };

  // Apply the solution-area filter on top of the release scope, then the date
  // sort — across the whole filtered set, BEFORE pagination.
  const visible = useMemo(
    () => sortByDocumentDate(solutionFilter(filtered, activeSolution), sortDir),
    [filtered, activeSolution, sortDir],
  );

  const invalidate = (): Promise<void> =>
    qc.invalidateQueries({ queryKey: ["docs"] }).then(() => undefined);

  const remove = useMutation({
    mutationFn: (id: number | string) => deleteDoc(id),
    onSuccess: () => void invalidate(),
  });
  const bulkRemove = useMutation({
    mutationFn: (ids: number[]) => bulkDeleteDocs(ids),
    onSuccess: () => {
      bulk.clear();
      setConfirmBulk(false);
      void invalidate();
    },
    onError: (err) => {
      console.error("[TypeReleaseDocsPage] bulk doc delete failed", err);
    },
  });
  const publish = useMutation({
    mutationFn: (idOrSlug: number | string) => publishDoc(idOrSlug),
    onSuccess: () => void invalidate(),
  });
  const unpublish = useMutation({
    mutationFn: (idOrSlug: number | string) => unpublishDoc(idOrSlug),
    onSuccess: () => void invalidate(),
  });
  // Inline solution-area re-tag from the row pill — a minimal PATCH that
  // touches only `solution_area_ids` (the many-to-many join; leaves
  // title/body/category untouched).
  const changeSolution = useMutation({
    mutationFn: (vars: { slug: string; ids: number[] }) =>
      updateDoc(vars.slug, { solution_area_ids: vars.ids }),
    onSuccess: () => void invalidate(),
  });

  // Move = a PATCH of `document_category_id` only, one document at a time
  // so a failure names how far it got; every write is the author's own
  // (the server enforces the same own-records rule).
  const moveDocs = async (ids: number[], folderId: number): Promise<void> => {
    let moved = 0;
    try {
      for (const id of ids) {
        await updateDoc(id, { document: { document_category_id: folderId } });
        moved += 1;
      }
    } finally {
      if (moved > 0) {
        bulk.clear();
        void invalidate();
      }
    }
  };

  // Bulk download (2026-09-23): the selection, or the whole folder, as ONE
  // zip (a single document saves as itself). `zipProgress` drives the label.
  const [zipProgress, setZipProgress] = useState<string | null>(null);
  const downloadMany = async (ids: number[]): Promise<void> => {
    if (ids.length === 0 || zipProgress) return;
    setZipProgress(`0/${ids.length}`);
    try {
      const out = await buildBulkDownload(ids, title, {
        onProgress: (done, total) => setZipProgress(`${done}/${total}`),
      });
      if (!out) {
        toast({ title: "Download failed", description: "None of the selected documents could be downloaded." });
        return;
      }
      await saveBlob(out.filename, out.blob);
      if (out.failed.length > 0) {
        toast({ title: "Some documents were skipped", description: out.failed.join(", ") });
      }
    } catch (err) {
      toast({ title: "Download failed", description: err instanceof Error ? err.message : "Could not build the zip" });
    } finally {
      setZipProgress(null);
    }
  };

  const [pendingDelete, setPendingDelete] = useState<{ id: number; title: string } | null>(null);
  const [confirmBulk, setConfirmBulk] = useState(false);
  // The ONE "Share to chat" dialog for the whole list (mirrors `pendingDelete`)
  // — a per-row kebab "Share to chat" sets the doc; not one dialog per row.
  const [shareChatDoc, setShareChatDoc] = useState<DocSummary | null>(null);

  // Only docs the user may modify are bulk-selectable. For a full editor that
  // is every row; for a create-tier author it's the ones they wrote. Driving
  // selection off this subset means "select all" + bulk delete can never touch
  // a doc the user doesn't own (the portal-api backend enforces the same, but
  // the UI shouldn't even offer it).
  const modifiable = useMemo(() => visible.filter((d) => canModify(d)), [visible, canModify]);

  // Selection has to live outside the rendered <ul> so it survives doc
  // status toggles (publish/unpublish re-render the row but shouldn't
  // drop the checkbox state). `useBulkSelection` auto-prunes any id
  // that scrolls off `modifiable`, so deletes don't leave dangling
  // selections either.
  //
  // Selection tracks the full modifiable set (not just the current page),
  // so a "Select all" check + page-flip preserves the user's intent —
  // same Gmail-style behaviour as the support ticket list.
  const bulk = useBulkSelection(modifiable);

  // Paginate the visible rows at 25/page. Bulk selection still spans
  // every page above, so checking "Select all" on page 1 keeps page 2's
  // rows selected too.
  const docPaging = usePagedList(visible, 25);

  const typeLabel    = isUncategorized ? "Uncategorized" : (category?.name ?? slug);
  const releaseLabel = isUnversioned ? "Library" : (release ? `v${release.version}` : releaseId);
  const isVersion    = release?.parent_release_id != null;
  const releaseTitle = `${isVersion ? "Version" : "Release"} ${release?.version ?? filtered[0]?.default_release?.version ?? releaseId}`;
  const title = releaseMode ? releaseTitle : isUnversioned ? typeLabel : `${typeLabel} · ${releaseLabel}`;
  const addLabel = releaseMode ? (isVersion ? "+ Add to this version" : "+ Add to this release") : "+ Add to this folder";

  const newDocHref = useMemo(() => {
    const params = new URLSearchParams();
    if (!releaseMode && !isUncategorized && category?.id) params.set("category_id", String(category.id));
    if (!isUnversioned && release?.id)    params.set("default_release_id", String(release.id));
    const qs = params.toString();
    return qs ? `/documentation/manage/new?${qs}` : "/documentation/manage/new";
  }, [releaseMode, isUncategorized, isUnversioned, category, release]);

  return (
    <Box sx={{ display: "grid", gap: 3 }} data-testid={releaseMode ? "release-docs-page" : "library-folder-page"}>
      {/* Title + breadcrumbs as one tight cluster: the dense header drops
          its bottom margin and this 8px gap puts the breadcrumbs right
          under the title (the subtitle blurb was removed). */}
      <Box sx={{ display: "grid", gap: 1 }}>
        <PageHeader
          dense
          title={title}
          right={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <BackButton
                to={releaseMode || isUnversioned ? "/documentation" : `/documentation/type/${encodeURIComponent(slug)}`}
                label={releaseMode || isUnversioned ? "Documentation" : "Releases"}
              />
              {!releaseMode && category && (
                <FolderShareMenu
                  folder={{
                    slug: category.slug,
                    name: category.name,
                    ...(category.visible_in_customer_portal != null
                      ? { visible_in_customer_portal: category.visible_in_customer_portal }
                      : {}),
                  }}
                />
              )}
              {canCreate && (
                <Button component={RouterLink} to={newDocHref} variant="contained">
                  {addLabel}
                </Button>
              )}
            </Box>
          }
        />

        <Box component="nav" sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", fontSize: 12, color: "text.disabled" }}>
          <MuiLink component={RouterLink} to="/documentation" underline="hover" sx={{ color: "inherit", "&:hover": { color: "text.primary" } }}>
            Documentation
          </MuiLink>
          <Box component="span" sx={{ mx: 0.75 }}>/</Box>
          {releaseMode ? (
            <>
              {release?.product_name && (
                <>
                  <Box component="span">{release.product_name}</Box>
                  <Box component="span" sx={{ mx: 0.75 }}>/</Box>
                </>
              )}
              {isVersion && line && (
                <>
                  <MuiLink
                    component={RouterLink}
                    to={`/documentation/release/${line.id}`}
                    underline="hover"
                    sx={{ color: "inherit", "&:hover": { color: "text.primary" } }}
                  >
                    Release {line.version}
                  </MuiLink>
                  <Box component="span" sx={{ mx: 0.75 }}>/</Box>
                </>
              )}
              <Box component="span" sx={{ color: "text.primary" }}>{releaseTitle}</Box>
              <Chip
                size="small"
                variant="outlined"
                component={RouterLink}
                to={
                  line
                    ? `/crm/releases/${encodeURIComponent(line.version)}?version=${encodeURIComponent(release?.version ?? "")}`
                    : release
                      ? `/crm/releases/${encodeURIComponent(release.version)}`
                      : "/crm/releases"
                }
                clickable
                label="Release notes & disk images"
                sx={{ ml: "auto" }}
              />
            </>
          ) : (
            <>
              {ancestorFolders.map((folder) => (
                <Fragment key={folder.id}>
                  <MuiLink
                    component={RouterLink}
                    to={`/documentation/type/${encodeURIComponent(folder.slug)}`}
                    underline="hover"
                    data-testid="folder-parent-crumb"
                    sx={{ color: "inherit", "&:hover": { color: "text.primary" } }}
                  >
                    {folder.name}
                  </MuiLink>
                  <Box component="span" sx={{ mx: 0.75 }}>/</Box>
                </Fragment>
              ))}
              {isUnversioned ? (
                <Box component="span">Library</Box>
              ) : (
                <MuiLink
                  component={RouterLink}
                  to={`/documentation/type/${encodeURIComponent(slug)}`}
                  underline="hover"
                  sx={{ color: "inherit", "&:hover": { color: "text.primary" } }}
                >
                  {typeLabel}
                </MuiLink>
              )}
              <Box component="span" sx={{ mx: 0.75 }}>/</Box>
              <Box component="span" sx={{ color: "text.primary" }}>{isUnversioned ? typeLabel : releaseLabel}</Box>
            </>
          )}
        </Box>

        {releaseMode && (
          <Typography data-testid="release-shelf-hint" sx={{ fontSize: 12, color: "text.secondary" }}>
            Release documentation — visible automatically to customers on this release and to every
            reseller; the folder a document is filed under does not gate it here.
            {isVersion && line ? ` Guides written against release ${line.version} are inherited below.` : ""}
          </Typography>
        )}
      </Box>

      {/* Solution-area switcher — only shown when the folder has docs to
          switch between. State lives in `?solution=` (see `setSolution`). */}
      {filtered.length > 0 && (
        <SolutionSwitcher
          options={options}
          counts={counts}
          active={activeSolution}
          onSelect={setSolution}
          {...(canEditAll ? { manageHref: "/documentation/solution-areas" } : {})}
        />
      )}

      {modifiable.length > 0 && (
        <BulkActionBar count={bulk.count} onClear={bulk.clear}>
          <BulkButton disabled={zipProgress != null} onClick={() => void downloadMany([...bulk.selected])}>
            <FileDownloadOutlinedIcon sx={{ fontSize: 14 }} />
            {zipProgress ? `Downloading ${zipProgress}…` : `Download ${bulk.count} (zip)`}
          </BulkButton>
          {!releaseMode && (
            <BulkButton onClick={() => setMoveIds([...bulk.selected])}>
              <DriveFileMoveOutlinedIcon sx={{ fontSize: 14 }} />
              Move {bulk.count} {bulk.count === 1 ? "document" : "documents"}
            </BulkButton>
          )}
          <BulkButton
            variant="destructive"
            disabled={bulkRemove.isPending}
            onClick={() => setConfirmBulk(true)}
          >
            <DeleteOutlineIcon sx={{ fontSize: 14 }} />
            Delete {bulk.count} {bulk.count === 1 ? "document" : "documents"}
          </BulkButton>
        </BulkActionBar>
      )}

      {modifiable.length > 0 && bulkRemove.isError && (
        <Box
          role="alert"
          sx={{
            borderRadius: 1,
            border: 1,
            borderColor: "error.main",
            bgcolor: (t) => alpha(t.palette.error.main, 0.08),
            px: 2,
            py: 1.5,
            fontSize: 14,
            color: "error.main",
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 500 }}>Bulk delete failed</Typography>
          <Typography sx={{ fontSize: 12, opacity: 0.8 }}>
            {bulkRemove.error instanceof Error
              ? bulkRemove.error.message
              : "Unknown error. Check the browser console + dashboard server logs."}
          </Typography>
        </Box>
      )}

      {!releaseMode && subFolders.length > 0 && (
        <SubFolderList
          folders={subFolders.map((f) => ({ id: f.id, slug: f.slug, name: f.name, count: subFolderCounts.get(f.id) ?? 0 }))}
        />
      )}

      {isLoading && <PageLoader />}

      {!isLoading && filtered.length === 0 && (releaseMode || subFolders.length === 0) && (
        <Box
          sx={{
            borderRadius: 1,
            border: 1,
            borderColor: "divider",
            bgcolor: (t) => alpha(t.palette.surfaceSoft, 0.4),
            p: 4,
            textAlign: "center",
            fontSize: 14,
            color: "text.disabled",
          }}
        >
          {releaseMode ? (
            <>No documentation pinned to <Box component="strong">{releaseTitle}</Box> yet.</>
          ) : isUnversioned ? (
            <>No documents in <Box component="strong">{typeLabel}</Box> yet.</>
          ) : (
            <>
              No documents in <Box component="strong">{typeLabel}</Box> for{" "}
              <Box component="strong">{releaseLabel}</Box> yet.
            </>
          )}
          {canCreate && (
            <>
              {" "}
              <MuiLink component={RouterLink} to={newDocHref} underline="always" sx={{ color: "text.primary" }}>
                Add the first one
              </MuiLink>
              .
            </>
          )}
        </Box>
      )}

      {/* Folder has docs, but the active solution filter hides them all. */}
      {!isLoading && filtered.length > 0 && visible.length === 0 && (
        <Box
          data-testid="solution-empty"
          sx={{
            borderRadius: 1,
            border: 1,
            borderColor: "divider",
            bgcolor: (t) => alpha(t.palette.surfaceSoft, 0.4),
            p: 4,
            textAlign: "center",
            fontSize: 14,
            color: "text.disabled",
          }}
        >
          No{" "}
          <Box component="strong">
            {activeSolution === "none"
              ? "uncategorized"
              : (areas.find((a) => a.slug === activeSolution)?.name ?? activeSolution)}
          </Box>{" "}
          documents in this folder.{" "}
          <Box
            component="button"
            type="button"
            onClick={() => setSolution("all")}
            sx={{ border: 0, bgcolor: "transparent", p: 0, font: "inherit", color: "text.primary", cursor: "pointer", textDecoration: "underline" }}
          >
            Show all
          </Box>
          .
        </Box>
      )}

      {!isLoading && visible.length > 0 && (
        <Box
          component="ul"
          data-testid="docs-list"
          sx={{
            listStyle: "none",
            m: 0,
            p: 0,
            overflow: "hidden",
            borderRadius: 1,
            border: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
            boxShadow: (t) => t.shadows[1],
          }}
        >
          {/* List header row: the Gmail-style select-all (only when the user
              has a modifiable doc here) on the left, and the sortable "Created"
              column header on the right (a read action — shown to viewers too).
              Clicking "Created" cycles unsorted → newest → oldest. */}
          <Box
            component="li"
            data-testid="docs-header-row"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              borderBottom: 1,
              borderColor: "divider",
              bgcolor: (t) => alpha(t.palette.surfaceSoft, 0.5),
              px: 2,
              py: 1,
              fontSize: 12,
              color: "text.disabled",
            }}
          >
            {modifiable.length > 0 && (
              <Box
                data-testid="docs-select-all-row"
                sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
              >
                <BulkCheckbox
                  checked={bulk.allOnPageChecked}
                  indeterminate={bulk.someOnPageChecked}
                  onChange={(checked) => bulk.toggleAllOnPage(checked)}
                  ariaLabel="Select all documents"
                />
                <Box component="span">{`Select all (${modifiable.length})`}</Box>
              </Box>
            )}
            <Box
              component="button"
              type="button"
              data-testid="docs-download-all"
              disabled={zipProgress != null}
              onClick={() => void downloadMany(visible.map((d) => d.id))}
              sx={{
                ml: "auto",
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                border: 0,
                bgcolor: "transparent",
                p: 0,
                font: "inherit",
                fontSize: 12,
                color: "text.secondary",
                cursor: "pointer",
                "&:hover": { color: "text.primary" },
              }}
            >
              <FileDownloadOutlinedIcon sx={{ fontSize: 14 }} />
              {zipProgress ? `Downloading ${zipProgress}…` : `Download all (${visible.length})`}
            </Box>
            <Box
              component="button"
              type="button"
              onClick={cycleSort}
              data-testid="docs-sort-created"
              aria-label={
                sortDir === "date_desc"
                  ? "Created date, newest first — click to sort oldest first"
                  : sortDir === "date_asc"
                    ? "Created date, oldest first — click to clear sorting"
                    : "Sort by created date, newest first"
              }
              sx={{
                ml: 2,
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                border: 0,
                bgcolor: "transparent",
                p: 0,
                font: "inherit",
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: 0.2,
                textTransform: "uppercase",
                color: sortDir ? "text.primary" : "text.disabled",
                cursor: "pointer",
                "&:hover": { color: "text.primary" },
              }}
            >
              Created
              <Box component="span" aria-hidden sx={{ fontSize: 11 }}>
                {sortDir === "date_desc" ? "▼" : sortDir === "date_asc" ? "▲" : "↕"}
              </Box>
            </Box>
          </Box>
          {docPaging.paged.map((d) => (
            <DocRow
              key={d.id}
              doc={d}
              canModify={canModify(d)}
              solutionAreas={areas}
              selected={bulk.isSelected(d.id)}
              onSelectChange={(checked, opts) => bulk.setChecked(d.id, checked, opts)}
              solutionBusy={changeSolution.isPending && changeSolution.variables?.slug === d.slug}
              onPickSolution={(ids) => changeSolution.mutate({ slug: d.slug, ids })}
              pdfBusy={pdf.busySlug === d.slug}
              onPdf={() => void pdf.download(d.slug)}
              onPublish={() => publish.mutate(d.id)}
              onUnpublish={() => unpublish.mutate(d.id)}
              onDelete={() => setPendingDelete({ id: d.id, title: d.title })}
              onShareToChat={() => setShareChatDoc(d)}
              {...(!releaseMode ? { onMove: () => setMoveIds([d.id]) } : {})}
            />
          ))}
        </Box>
      )}

      {!isLoading && visible.length > 0 && (
        <Pagination
          page={docPaging.page}
          perPage={docPaging.perPage}
          totalCount={docPaging.totalCount}
          totalPages={docPaging.totalPages}
          entityNoun="documents"
          hideIfSinglePage
          onPageChange={docPaging.setPage}
          onPerPageChange={docPaging.setPerPage}
        />
      )}

      {releaseMode && inherited.length > 0 && line && (
        <Box sx={{ display: "grid", gap: 1 }} data-testid="release-inherited-docs">
          <Typography component="h2" sx={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4, color: "text.secondary" }}>
            From release {line.version} — inherited by every version;{" "}
            <MuiLink component={RouterLink} to={`/documentation/release/${line.id}`} underline="hover">
              edit them on the release
            </MuiLink>
          </Typography>
          <Box
            component="ul"
            data-testid="release-inherited-list"
            sx={{
              listStyle: "none",
              m: 0,
              p: 0,
              overflow: "hidden",
              borderRadius: 1,
              border: 1,
              borderColor: "divider",
              bgcolor: "background.paper",
              boxShadow: (t) => t.shadows[1],
            }}
          >
            {inherited.map((d) => (
              <DocRow
                key={d.id}
                doc={d}
                canModify={false}
                solutionAreas={areas}
                selected={false}
                onSelectChange={() => undefined}
                solutionBusy={false}
                onPickSolution={() => undefined}
                pdfBusy={pdf.busySlug === d.slug}
                onPdf={() => void pdf.download(d.slug)}
                onPublish={() => undefined}
                onUnpublish={() => undefined}
                onDelete={() => undefined}
                onShareToChat={() => setShareChatDoc(d)}
              />
            ))}
          </Box>
        </Box>
      )}

      <ConfirmDialog
        isOpen={pendingDelete != null}
        onClose={() => setPendingDelete(null)}
        title="Delete document?"
        description={
          pendingDelete
            ? `"${pendingDelete.title}" will be soft-deleted and its files purged from object storage. The vector-DB entry is removed too. This cannot be undone.`
            : ""
        }
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={async () => {
          if (pendingDelete) await remove.mutateAsync(pendingDelete.id);
        }}
      />

      <BulkDeleteDialog
        isOpen={confirmBulk}
        onClose={() => setConfirmBulk(false)}
        items={visible
          .filter((d) => bulk.isSelected(d.id))
          .map((d) => ({ id: d.id, title: d.title, status: d.status }))}
        entityLabel={{ singular: "Document", plural: "Documents" }}
        cascadingWarning="Each document's attachments will be purged from object storage and its vector-DB entry removed. This cannot be undone."
        renderItem={(it) => <>{it.title}</>}
        renderItemBadge={(it) =>
          it.status === "published" ? (
            <Box component="span" sx={{ fontSize: "10px", color: "error.dark" }}>published</Box>
          ) : null
        }
        onConfirm={async () => {
          await bulkRemove.mutateAsync([...bulk.selected]);
        }}
      />

      <MoveToFolderDialog
        open={moveIds != null}
        count={moveIds?.length ?? 0}
        folders={folders}
        currentFolderId={category?.id ?? null}
        onClose={() => setMoveIds(null)}
        onMove={(folderId) => moveDocs(moveIds ?? [], folderId)}
      />

      {shareChatDoc && (
        <ShareToChatDialog
          open
          onClose={() => setShareChatDoc(null)}
          subject={documentShareSubject(shareChatDoc)}
        />
      )}
    </Box>
  );
}
